/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { chatSetPinned } from "../funcs/chatSetPinned.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetChatPinnedRequest,
  SetChatPinnedSecurity,
} from "../models/operations/setchatpinned.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ChatSetPinnedMutationVariables = {
  request: SetChatPinnedRequest;
  security?: SetChatPinnedSecurity | undefined;
  options?: RequestOptions;
};

export type ChatSetPinnedMutationData = void;

export type ChatSetPinnedMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setPinned chat
 *
 * @remarks
 * Pin or unpin a chat. Pinned chats surface in a dedicated section above recents on the chat page.
 */
export function useChatSetPinnedMutation(
  options?: MutationHookOptions<
    ChatSetPinnedMutationData,
    ChatSetPinnedMutationError,
    ChatSetPinnedMutationVariables
  >,
): UseMutationResult<
  ChatSetPinnedMutationData,
  ChatSetPinnedMutationError,
  ChatSetPinnedMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildChatSetPinnedMutation(client, options),
    ...options,
  });
}

export function mutationKeyChatSetPinned(): MutationKey {
  return ["@gram/client", "chat", "setPinned"];
}

export function buildChatSetPinnedMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ChatSetPinnedMutationVariables,
  ) => Promise<ChatSetPinnedMutationData>;
} {
  return {
    mutationKey: mutationKeyChatSetPinned(),
    mutationFn: function chatSetPinnedMutationFn({
      request,
      security,
      options,
    }): Promise<ChatSetPinnedMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(chatSetPinned(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
